import { on } from '@ember/modifier';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import didInsert from '@ember/render-modifiers/modifiers/did-insert';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import didUpdate from '@ember/render-modifiers/modifiers/did-update';
import { next } from '@ember/runloop';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import config from 'garaje/config/environment';
import htmlSafe from 'garaje/helpers/html-safe';
import type UploaderFactoryService from 'garaje/services/uploader-factory';
import flashPromise from 'garaje/utils/flash-promise';
import { localCopy } from 'tracked-toolbox';

const IS_TESTING = config.environment === 'test';

interface FileUploadSignature {
  Args: {
    accept: string;
    action?: (response: unknown, previewPromise: Promise<void | HTMLImageElement>) => void;
    allUploaded?: () => void;
    autoUpload?: boolean;
    beginUpload: () => void;
    color?: string;
    extra?: object;
    files: File[] | FileList;
    flashPromiseOff?: boolean;
    invalidBody?: string;
    invalidTitle?: string;
    max?: number;
    multiple?: boolean;
    namespace?: string;
    onError?: (error: unknown) => void;
    onPendingUploadChange?: (pendingFiles: object[], pendingUpload: object) => void;
    param?: string;
    requestType?: string;
    target?: HTMLElement | string;
    isDisabled?: boolean;
    url?: string;
    validSize?: number;
    validType?: string | RegExp;
  };
  Element: HTMLInputElement;
}

interface PendingFile {
  preview: HTMLImageElement | void;
  file: File;
  clear: () => void;
}

interface PendingUpload {
  upload: () => void;
  pendingFiles: PendingFile[];
  clear: () => void;
}

/**
 * Generates an HTMLInputElement containing a valid src from a file upload
 *
 * @param file the file to generate a preview for
 * @returns the image element
 */
function loadPreview(file: File): Promise<void | HTMLImageElement> {
  return new Promise(function (resolve, reject) {
    if (IS_TESTING) {
      return resolve();
    }

    const reader = new FileReader();

    reader.onload = function () {
      const image = new Image();

      image.onload = function () {
        resolve(image);
      };

      image.onerror = function () {
        // @ts-expect-error - existing code from before ts migration
        // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
        reject(image.errors);
      };

      image.src = reader.result as string;
    };

    reader.onerror = function () {
      // @ts-expect-error - existing code from before ts migration
      // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
      reject(reader.errors);
    };

    reader.readAsDataURL(file);
  });
}

export default class FileUpload extends Component<FileUploadSignature> {
  @service declare uploaderFactory: UploaderFactoryService;

  @localCopy('args.autoUpload', true) autoUpload!: boolean;

  @localCopy('args.max', 1) max!: number;
  @localCopy('args.param', 'file') param!: string;
  @localCopy('args.requestType', 'POST') requestType!: string;
  @localCopy('args.multiple', false) multiple!: boolean;
  @localCopy('args.validType') validType!: string | RegExp;
  @localCopy('args.validSize') validSize!: number;

  @tracked _files: File[] = [];
  @tracked _element!: HTMLInputElement;
  @tracked isValid = true;

  pendingUpload: PendingUpload | null = null;

  onInsert = (element: HTMLInputElement): void => {
    this._element = element;
  };

  onChange = (event: Event): void => {
    const input = event.target as HTMLInputElement;
    if (!isEmpty(input.files)) {
      void this.filesDidChange(input.files);
    }
  };

  onUpdate = (): void => {
    const oldFiles = this._files;
    const newFiles = this.args.files;
    if (newFiles && newFiles !== oldFiles) {
      // eslint-disable-next-line ember/no-runloop
      next(this, function () {
        void this.filesDidChange(newFiles);
      });
    }
  };

  async filesDidChange(files: FileList | File[] | null): Promise<void> {
    files = files ?? [];

    // HALT if any selected file is invalid (e.g. too big)
    for (let i = 0; i < files.length; i++) {
      if (!this.validate(files[i]!)) return;
    }

    // convert to array in case files is a FileList
    files = Array.from(files);

    if (!this.autoUpload) {
      // if we are not auto uploading and we are uploading multiple files, we need keep
      // building on the pending upload instead of resetting the upload state
      this._files = this.multiple ? [...(this._files ?? []), ...files] : files;

      // only generate pending file objects for new files
      const newPendingFiles = await this.generatePendingFiles(files);
      this.pendingUpload = await this.generatePendingUpload([...newPendingFiles]);

      // emit new pending files and pending upload object
      this.args.onPendingUploadChange?.(newPendingFiles, this.pendingUpload);
    } else {
      this._files = files;
      this.upload(files);
    }

    return;
  }

  async generatePendingFiles(files: File[]): Promise<PendingFile[]> {
    const promises = files.map(async (file) => {
      const preview = await loadPreview(file);
      return {
        preview,
        file,
        clear: () => {
          this._files.splice(this._files.indexOf(file), 1);
          // reset component state if we have cleared all files
          if (this._files.length === 0) this.clear();
        },
      };
    });

    return Promise.all(promises);
  }

  // eslint-disable-next-line @typescript-eslint/require-await
  async generatePendingUpload(pendingFiles: PendingFile[]): Promise<PendingUpload> {
    return {
      // use the internal list of files that is kept up to
      // date as the pendingUpload is updated with new or removed files
      upload: () => this.upload(this._files),
      pendingFiles: [...(this.pendingUpload?.pendingFiles ?? []), ...pendingFiles],
      clear: () => this.clear(),
    };
  }

  /**
   * Clears the file input and any pending upload
   */
  clear(): void {
    this._element.value = '';
    this.pendingUpload = null;
  }

  upload(files: File[]): void {
    const { url: uploadUrl, namespace, extra } = this.args;
    const { param, requestType: method } = this;

    const uploader = this.uploaderFactory.createUploader({
      url: uploadUrl,
      paramNamespace: namespace,
      paramName: param,
      method,
      headers: { Accept: 'application/vnd.api+json' },
    });

    const promises = [];
    const didUpload = (response: unknown) => {
      const previewPromise = loadPreview(files[0]!);
      this.args.action?.(response, previewPromise);
    };

    uploader.on('didUpload', didUpload);

    if (!isEmpty(files)) {
      const maxFiles = Math.min(this.max, files.length);

      for (let i = 0; i < maxFiles; i++) {
        this.args.beginUpload?.();
        promises.push(uploader.upload(files[i]!, extra));
      }
    }

    const allPromise = Promise.all(promises).finally(() => {
      this.args.allUploaded?.();
      if (this._element) {
        this._element.value = '';
      }
      uploader.off('didUpload', didUpload);
    });

    if (!this.args.flashPromiseOff) {
      flashPromise(allPromise, this.args.target);
    }

    const { onError } = this.args;
    if (onError) {
      allPromise.catch((error) => onError(error));
    }
  }

  get validTypeReg(): RegExp {
    if (this.validType instanceof RegExp) {
      return this.validType;
    }
    return new RegExp(this.validType);
  }

  validate(file: File): boolean {
    const typeValid = this.validType ? this.validTypeReg.test(file.type) : true;
    const sizeValid = this.validSize ? file.size <= this.validSize : true;

    this.isValid = typeValid && sizeValid;

    return this.isValid;
  }

  <template>
    <input
      data-test-file-upload-input
      disabled={{@isDisabled}}
      type="file"
      multiple={{this.multiple}}
      ...attributes
      {{didInsert this.onInsert}}
      {{on "change" this.onChange}}
      {{didUpdate this.onUpdate @files}}
    />

    {{#unless this.isValid}}
      <div class="my-4" data-test-user-document-invalid-message>
        <img
          src="/assets/images/circle-exclamation-red.svg"
          alt="something went wrong"
          class="mr-1 align-middle relative bg-white rounded-full"
          style="min-width: 24px; width:24px; height:24px;"
        />
        <span class="text-lg">{{if @invalidTitle @invalidTitle "File is invalid"}}</span>
        {{#if @invalidBody}}
          <span class="text-grey">{{htmlSafe @invalidBody}}</span>
        {{/if}}
      </div>
    {{/unless}}
  </template>
}
